import React, { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CheckCircle,
  XCircle,
  Clock,
  Flag,
  Eye,
  RefreshCw,
  Search,
  Image,
  Grid3X3,
  ThumbsUp,
  ThumbsDown,
  MoreHorizontal,
  ExternalLink
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useApprovalQueue, useApprovalCounts } from '@/features/approval/hooks';
import { useGalleryImages } from '@/features/gallery/hooks';
import ApprovalSwipeCard from '@/features/approval/components/cards/ApprovalSwipeCard';
import ApprovalStats from '@/features/approval/components/stats/ApprovalStats';
import { ApprovalActionDialog } from '@/features/approval/components/dialogs/ApprovalActionDialog';
import { ApprovalHistory } from '@/features/approval/components/history/ApprovalHistory';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { ApprovalFlow } from '@/components/navigation/ApprovalFlow';
import type { 
  ApprovalItem,
  FranchiseRegion,
  ReviewStatus,
  PhotoReviewStatus
} from '@/types/gallery';
import type { GalleryAllImage } from '@/lib/supabase';
import { buildJobberJobUrl } from '@/features/jobber';

// Franchise regions for filtering
const franchiseRegions: { value: FranchiseRegion; label: string }[] = [
  { value: 'HQ', label: 'Headquarters' },
  { value: 'toronto', label: 'Toronto' },
  { value: 'hamilton', label: 'Hamilton' },
  { value: 'mississauga', label: 'Mississauga' },
  { value: 'brampton', label: 'Brampton' },
  { value: 'north_york', label: 'North York' },
  { value: 'scarborough', label: 'Scarborough' },
  { value: 'london', label: 'London' },
  { value: 'general', label: 'General' }
];

const getJobberLink = (item: ApprovalItem): string | null => {
  const metadata = (item.metadata ?? {}) as { jobber_web_uri?: string | null; job_id?: string | number | null };
  return buildJobberJobUrl({
    jobberWebUri: metadata.jobber_web_uri,
    jobNumber: item.job_number,
    jobId: metadata.job_id
  });
};

export function ApprovalQueueContainer() {
  // State management
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<ApprovalItem | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'swipe' | 'list'>('swipe');
  const [activeTab, setActiveTab] = useState<'all' | 'galleries' | 'images'>('all');
  
  // Action dialog state
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [pendingActionItem, setPendingActionItem] = useState<ApprovalItem | null>(null);
  const [pendingAction, setPendingAction] = useState<'approve' | 'reject' | 'flag' | null>(null);
  const selectedJobberLink = useMemo(() => (selectedItem ? getJobberLink(selectedItem) : null), [selectedItem]);

  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { data: approvalCounts } = useApprovalCounts();
  
  // Get current filters from URL
  const currentFilters = useMemo(() => ({
    type: searchParams.get('type') as 'galleries' | 'images' | 'all' | undefined,
    region: searchParams.get('region') || undefined,
    status: searchParams.get('status') || undefined
  }), [searchParams]);

  // Fetch gallery images when a gallery is selected for detailed view
  const {
    data: galleryImages = [],
    isLoading: galleryImagesLoading,
    refetch: refetchGalleryImages
  } = useGalleryImages(selectedItem?.type === 'gallery' ? selectedItem.id : null);

  // Use the approval queue hook
  const {
    pendingGalleries,
    pendingImages,
    isLoading,
    refreshQueue,
    approveGallery,
    rejectGallery,
    flagGallery,
    approveImage,
    rejectImage,
    flagImage
  } = useApprovalQueue();

  // Transform data to ApprovalItem format for compatibility
  const allItems: ApprovalItem[] = useMemo(() => [
    ...pendingGalleries,
    ...pendingImages.map((image: GalleryAllImage): ApprovalItem => ({
      id: image.original_id!,
      type: 'image' as const,
      title: image.caption || 'Untitled Image',
      description: undefined,
      image_url: image.image_url || undefined,
      franchise_region: (image.franchise_region || 'general') as FranchiseRegion,
      review_status: image.review_status as ReviewStatus | PhotoReviewStatus,
      created_at: image.created_at || new Date().toISOString(),
      job_number: image.job_number,
      service_name: undefined,
      metadata: {
        image_type: image.image_type,
        job_id: (image as any).job_id, // job_id might be on the object if it's a job image
        jobber_web_uri: image.jobber_web_uri
      }
    }))
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()), [pendingGalleries, pendingImages]);

  // Action handlers with attribution support
  const handleApprove = useCallback(async (item: ApprovalItem, reason?: string): Promise<boolean> => {
    try {
      if (item.type === 'gallery') {
        return await approveGallery(item.id, reason);
      } else {
        const imageType = item.metadata?.image_type as 'gallery_photo' | 'job_image' | undefined;
        return await approveImage(item.id, imageType, reason);
      }
    } catch (error) {
      console.error('Error approving item:', error);
      return false;
    }
  }, [approveGallery, approveImage]);

  const handleReject = useCallback(async (item: ApprovalItem, reason?: string): Promise<boolean> => {
    try {
      if (item.type === 'gallery') {
        return await rejectGallery(item.id, reason);
      } else {
        const imageType = item.metadata?.image_type as 'gallery_photo' | 'job_image' | undefined;
        return await rejectImage(item.id, imageType, reason);
      }
    } catch (error) {
      console.error('Error rejecting item:', error);
      return false;
    }
  }, [rejectGallery, rejectImage]);

  const handleFlag = useCallback(async (item: ApprovalItem, reason?: string): Promise<boolean> => {
    try {
      if (item.type === 'gallery') {
        return await flagGallery(item.id, reason);
      } else {
        const imageType = item.metadata?.image_type as 'gallery_photo' | 'job_image' | undefined;
        return await flagImage(item.id, imageType, reason);
      }
    } catch (error) {
      console.error('Error flagging item:', error);
      return false;
    }
  }, [flagGallery, flagImage]);

  const handleView = useCallback((item: ApprovalItem) => {
    setSelectedItem(item);
    setIsDetailDialogOpen(true);
  }, []);

  const handleShare = useCallback((item: ApprovalItem) => {
    if (item.type === 'gallery') {
      // Navigate to social media creation with this gallery
      toast({
        title: 'Share Feature',
        description: 'Social media sharing integration coming soon',
        variant: 'default'
      });
    }
  }, [toast]);

  // Action dialog handlers
  const handleActionClick = (item: ApprovalItem, action: 'approve' | 'reject' | 'flag') => {
    if (action === 'approve') {
      // Bypass dialog for approvals – execute immediately
      void (async () => {
        const success = await handleApprove(item);
        if (success) {
          await refreshQueue();
          toast({
            title: 'Success',
            description: `${item.type} approved successfully`,
            variant: 'default'
          });
        } else {
          toast({
            title: 'Error',
            description: `Failed to approve ${item.type}`,
            variant: 'destructive'
          });
        }
      })();
      return;
    }
    setPendingActionItem(item);
    setPendingAction(action);
    setShowActionDialog(true);
  };

  const handleActionConfirm = async (action: 'approve' | 'reject' | 'flag', reason?: string) => {
    if (!pendingActionItem) return;

    let success = false;
    switch (action) {
      case 'approve':
        success = await handleApprove(pendingActionItem, reason);
        break;
      case 'reject':
        success = await handleReject(pendingActionItem, reason);
        break;
      case 'flag':
        success = await handleFlag(pendingActionItem, reason);
        break;
    }

    if (success) {
      await refreshQueue();
      toast({
        title: 'Success',
        description: `${pendingActionItem.type} ${action}d successfully`,
        variant: 'default'
      });
    }

    setShowActionDialog(false);
    setPendingActionItem(null);
    setPendingAction(null);
  };

  const handleDialogClose = () => {
    setShowActionDialog(false);
    setPendingActionItem(null);
    setPendingAction(null);
  };

  const handleBulkApprove = async () => {
    if (selectedItems.size === 0) return;
    
    // Convert set to array of items for bulk op
    const items = Array.from(selectedItems).map(itemKey => {
      const [type, id] = itemKey.split('-');
      // Find the item to get metadata (needed for image type)
      const item = allItems.find(i => i.type === type && i.id === parseInt(id));
      return item;
    }).filter(Boolean) as ApprovalItem[];

    try {
      // We need to implement bulk approve in the hook or just loop here
      // For now, loop
      let successCount = 0;
      for (const item of items) {
        if (await handleApprove(item)) {
          successCount++;
        }
      }

      toast({
        title: 'Success',
        description: `${successCount} items approved successfully`,
        variant: 'default'
      });
      setSelectedItems(new Set());
      await refreshQueue();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to approve some items',
        variant: 'destructive'
      });
    }
  };

  const toggleSelection = (item: ApprovalItem) => {
    const itemKey = `${item.type}-${item.id}`;
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey);
      } else {
        newSet.add(itemKey);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    const allItemKeys = filteredItems.map(item => `${item.type}-${item.id}`);
    setSelectedItems(new Set(allItemKeys));
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  const getStatusBadge = (status: ReviewStatus | PhotoReviewStatus) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-chart-5/10 text-chart-5"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-destructive/10 text-destructive"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      case 'under_review':
        return <Badge className="bg-chart-4/10 text-chart-4"><Flag className="w-3 h-3 mr-1" />Under Review</Badge>;
      default:
        return <Badge className="bg-accent/10 text-accent"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const getRegionBadge = (region: FranchiseRegion) => {
    const regionInfo = franchiseRegions.find(r => r.value === region);
    return <Badge variant="outline">{regionInfo?.label || region}</Badge>;
  };

  // Filter items by active tab and search
  const filteredItems = useMemo(() => {
    let filtered = allItems;

    // Filter by tab
    if (activeTab === 'galleries') {
      filtered = filtered.filter(item => item.type === 'gallery');
    } else if (activeTab === 'images') {
      filtered = filtered.filter(item => item.type === 'image');
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(item => 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.description || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [allItems, activeTab, searchQuery]);

  // Calculate stats for display (not database fetch)
  const stats = useMemo(() => ({
    total_pending: allItems.length,
    pending_galleries: pendingGalleries.length,
    pending_images: pendingImages.length,
  }), [allItems, pendingGalleries, pendingImages]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Breadcrumb Navigation */}
      <div>
        <Breadcrumb />
      </div>

      {/* Approval Flow Navigation */}
      <div>
        <ApprovalFlow
          currentPage="approval-queue"
          pendingCount={approvalCounts?.totalPending || 0}
          filters={currentFilters}
        />
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Approval Queue</h1>
          <p className="text-gray-600">Review and approve galleries and images across all franchises</p>
          {(currentFilters.type || currentFilters.region) && (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {currentFilters.type && (
                <Badge variant="secondary">{currentFilters.type}</Badge>
              )}
              {currentFilters.region && (
                <Badge variant="secondary">{currentFilters.region}</Badge>
              )}
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => refreshQueue()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Statistics */}
      {/* Pass stats to avoid re-fetching if possible, otherwise it will use hook */}
      <ApprovalStats />

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              
              <Select value="all" onValueChange={(_value) => {}}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {franchiseRegions.map(region => (
                    <SelectItem key={region.value} value={region.value}>
                      {region.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedItems.size > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{selectedItems.size} selected</Badge>
                  <Button size="sm" variant="outline" onClick={clearSelection}>
                    Clear
                  </Button>
                  <Button size="sm" variant="outline" onClick={selectAll}>
                    Select All
                  </Button>
                  <Button size="sm" variant="default" onClick={handleBulkApprove}>
                    Approve Selected
                  </Button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'swipe' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('swipe')}
                title="Swipe View (Mobile-friendly)"
              >
                <ThumbsUp className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
                title="Grid View"
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
                title="List View"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)}>
        <TabsList>
          <TabsTrigger value="all">All ({stats.total_pending})</TabsTrigger>
          <TabsTrigger value="galleries">Galleries ({stats.pending_galleries})</TabsTrigger>
          <TabsTrigger value="images">Images ({stats.pending_images})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredItems.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <CheckCircle className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">All caught up!</h3>
                <p className="text-gray-600">No items pending approval at the moment.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {viewMode === 'swipe' && (
                <div className="space-y-6">
                  {filteredItems.map((item) => (
                    <ApprovalSwipeCard
                      key={`${item.type}-${item.id}`}
                      item={item}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onFlag={handleFlag}
                      onView={handleView}
                      onShare={handleShare}
                      showApprovalHistory={true}
                    />
                  ))}
                </div>
              )}
              
              {viewMode !== 'swipe' && (
                <div className={cn(
                  viewMode === 'grid' 
                    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                    : "space-y-4"
                )}>
                  {filteredItems.map((item) => {
                    const itemKey = `${item.type}-${item.id}`;
                    const isSelected = selectedItems.has(itemKey);
                    const jobberLink = getJobberLink(item);

                    return (
                      <Card 
                        key={itemKey} 
                        className={cn(
                          "hover:shadow-md transition-shadow cursor-pointer",
                          isSelected && "ring-2 ring-blue-500"
                        )}
                        onClick={() => toggleSelection(item)}
                      >
                    {viewMode === 'grid' ? (
                      <CardContent className="p-4">
                        {/* Image/Preview */}
                        <div className="aspect-video bg-gray-100 rounded-lg mb-3 overflow-hidden relative">
                          {item.image_url ? (
                            <img
                              src={item.image_url}
                              alt={item.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.parentElement?.querySelector('.fallback-icon')?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <div className="fallback-icon hidden absolute inset-0 flex items-center justify-center">
                            {item.type === 'gallery' ? (
                              <Grid3X3 className="h-12 w-12 text-gray-400" />
                            ) : (
                              <Image className="h-12 w-12 text-gray-400" />
                            )}
                          </div>
                          
                          {/* Type badge */}
                          <div className="absolute top-2 left-2">
                            <Badge variant={item.type === 'gallery' ? 'default' : 'secondary'}>
                              {item.type === 'gallery' ? 'Gallery' : 'Image'}
                            </Badge>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="space-y-3">
                          <div>
                            <h3 className="font-semibold text-sm truncate">{item.title}</h3>
                            {item.description && (
                              <p className="text-xs text-gray-600 line-clamp-2 mt-1">
                                {item.description}
                              </p>
                            )}
                          </div>

                          <div className="flex items-center gap-2">
                            {getRegionBadge(item.franchise_region)}
                            {item.job_number && (
                              <Badge variant="outline" className="text-xs">
                                Job #{item.job_number}
                              </Badge>
                            )}
                            {item.type === 'image' && jobberLink && (
                              <a
                                href={jobberLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Open Jobber job in new tab"
                                className="ml-auto inline-flex items-center text-xs text-chart-4 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Jobber <ExternalLink className="h-3 w-3 ml-1" />
                              </a>
                            )}
                          </div>

                          {item.service_name && (
                            <Badge variant="outline" className="text-xs bg-chart-4/10 text-chart-4">
                              {item.service_name}
                            </Badge>
                          )}

                          <div className="text-xs text-gray-500">
                            {new Date(item.created_at).toLocaleDateString()}
                          </div>

                          {/* Actions */}
                          <div className="flex justify-between items-center pt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedItem(item);
                                setIsDetailDialogOpen(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            
                            <div className="flex space-x-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-chart-5 hover:bg-chart-5/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleActionClick(item, 'approve');
                                }}
                              >
                                <ThumbsUp className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-destructive hover:bg-destructive/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleActionClick(item, 'reject');
                                }}
                              >
                                <ThumbsDown className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-accent hover:bg-accent/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleActionClick(item, 'flag');
                                }}
                              >
                                <Flag className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    ) : (
                      // List view
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            {item.image_url ? (
                              <img
                                src={item.image_url}
                                alt={item.title}
                                className="w-16 h-16 object-cover rounded"
                              />
                            ) : (
                              <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                                {item.type === 'gallery' ? (
                                  <Grid3X3 className="h-8 w-8 text-gray-400" />
                                ) : (
                                  <Image className="h-8 w-8 text-gray-400" />
                                )}
                              </div>
                            )}
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium truncate">{item.title}</h3>
                                <Badge variant={item.type === 'gallery' ? 'default' : 'secondary'} className="text-xs">
                                  {item.type}
                                </Badge>
                              </div>
                              
                              {item.description && (
                                <p className="text-sm text-gray-600 truncate mb-2">
                                  {item.description}
                                </p>
                              )}
                              
                              <div className="flex items-center gap-2 text-xs">
                                {getRegionBadge(item.franchise_region)}
                                {item.job_number && (
                                  <Badge variant="outline" className="text-xs">
                                    Job #{item.job_number}
                                  </Badge>
                                )}
                                <span className="text-gray-500">
                                  {new Date(item.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedItem(item);
                                setIsDetailDialogOpen(true);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {item.type === 'image' && jobberLink && (
                              <Button asChild size="sm" variant="outline">
                                <a
                                  href={jobberLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label="Open Jobber job in new tab"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              </Button>
                            )}
                            <Button
                              size="sm"
                              className="bg-chart-5 hover:bg-chart-5/90 text-primary-foreground"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleActionClick(item, 'approve');
                              }}
                            >
                              <ThumbsUp className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleActionClick(item, 'reject');
                              }}
                            >
                              <ThumbsDown className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>


      {/* Detail View Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedItem?.title}</DialogTitle>
            <DialogDescription>
              {selectedItem?.type === 'gallery' ? 'Gallery' : 'Image'} details and metadata
            </DialogDescription>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-6">
              {/* Preview */}
              {selectedItem.image_url && (
                <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={selectedItem.image_url}
                    alt={selectedItem.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Gallery Images Section - Only show for galleries */}
              {selectedItem.type === 'gallery' && (
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Grid3X3 className="h-4 w-4" />
                    Gallery Images ({galleryImages.length})
                  </h4>
                  
                  {galleryImagesLoading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="aspect-square bg-gray-200 animate-pulse rounded-lg"></div>
                      ))}
                    </div>
                  ) : galleryImages.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-64 overflow-y-auto">
                      {galleryImages.map((image) => (
                        <div key={`${image.image_type}-${image.original_id}`} className="relative group">
                          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                            <img
                              src={image.image_url ?? undefined}
                              alt={image.caption || 'Gallery image'}
                              className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                              onClick={() => image.image_url && window.open(image.image_url, '_blank')}
                            />
                          </div>
                          
                          {/* Image type badge */}
                          <div className="absolute top-1 left-1">
                            <Badge variant={image.image_type === 'gallery_photo' ? 'default' : 'secondary'} className="text-xs">
                              {image.image_type === 'gallery_photo' ? 'Direct' : 'Job'}
                            </Badge>
                          </div>
                          
                          {/* Review status for job images */}
                          {image.image_type === 'job_image' && (
                            <div className="absolute top-1 right-1">
                              <Badge 
                                variant={image.review_status === 'approved' ? 'default' : 'secondary'}
                                className={cn(
                                  "text-xs",
                                  image.review_status === 'approved' && "bg-chart-5/10 text-chart-5",
                                  image.review_status === 'pending' && "bg-accent/10 text-accent",
                                  image.review_status === 'rejected' && "bg-destructive/10 text-destructive"
                                )}
                              >
                                {image.review_status}
                              </Badge>
                            </div>
                          )}
                          
                          {/* Caption overlay */}
                          {image.caption && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              {image.caption}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Grid3X3 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>No images in this gallery yet</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">Basic Information</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Type:</strong> {selectedItem.type}</div>
                    <div><strong>Title:</strong> {selectedItem.title}</div>
                    <div><strong>Region:</strong> {getRegionBadge(selectedItem.franchise_region)}</div>
                    {selectedItem.job_number && (
                      <div><strong>Job Number:</strong> #{selectedItem.job_number}</div>
                    )}
                    {selectedItem.type === 'image' && selectedJobberLink && (
                      <div>
                        <strong>Jobber:</strong> 
                        <a
                          href={selectedJobberLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Open Jobber job in new tab"
                          className="inline-flex items-center text-blue-600 hover:underline ml-1"
                        >
                          Open Job <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      </div>
                    )}
                    <div><strong>Created:</strong> {new Date(selectedItem.created_at).toLocaleString()}</div>
                    <div><strong>Status:</strong> {getStatusBadge(selectedItem.review_status)}</div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-3">Additional Details</h4>
                  <div className="space-y-2 text-sm">
                    {selectedItem.service_name && (
                      <div><strong>Service:</strong> {selectedItem.service_name}</div>
                    )}
                    {selectedItem.description && (
                      <div>
                        <strong>Description:</strong>
                        <p className="mt-1 text-gray-600">{selectedItem.description}</p>
                      </div>
                    )}
                    {selectedItem.metadata && (
                      <div>
                        <strong>Metadata:</strong>
                        <pre className="mt-1 text-xs bg-gray-50 p-2 rounded">
                          {JSON.stringify(selectedItem.metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Approval Summary for Galleries */}
              {selectedItem.type === 'gallery' && galleryImages.length > 0 && (
                <div className="bg-chart-4/10 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 text-chart-4">Approval Summary</h4>
                  <div className="flex gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-chart-5 rounded-full"></div>
                      <span>{galleryImages.filter(img => img.review_status === 'approved').length} Approved</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-accent rounded-full"></div>
                      <span>{galleryImages.filter(img => img.review_status === 'pending').length} Pending</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-destructive rounded-full"></div>
                      <span>{galleryImages.filter(img => img.review_status === 'rejected').length} Rejected</span>
                    </div>
                  </div>
                  {galleryImages.some(img => img.review_status === 'pending') && (
                    <p className="text-chart-4 text-sm mt-2">
                      ⚠️ This gallery contains {galleryImages.filter(img => img.review_status === 'pending').length} pending images. Consider reviewing them first.
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-sm text-gray-500">
                  {selectedItem.type === 'gallery' ? 'Approve this gallery to make it public' : 'Review this image for approval'}
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    className="text-chart-5 hover:bg-chart-5/10"
                    onClick={async () => {
                      const success = await handleApprove(selectedItem);
                      if (success) {
                        // Refresh gallery images if we just approved a gallery
                        if (selectedItem.type === 'gallery') {
                          await refetchGalleryImages();
                        }
                        // Refresh the main approval queue
                        await refreshQueue();
                        setIsDetailDialogOpen(false);
                        toast({
                          title: 'Success',
                          description: `${selectedItem.type} approved successfully`,
                          variant: 'default'
                        });
                      }
                    }}
                  >
                    <ThumbsUp className="w-4 h-4 mr-2" />
                    Approve {selectedItem.type === 'gallery' ? 'Gallery' : 'Image'}
                  </Button>
                  <Button
                    variant="outline"
                    className="text-destructive hover:bg-destructive/10"
                    onClick={async () => {
                      const success = await handleReject(selectedItem);
                      if (success) {
                        // Refresh gallery images if we just rejected a gallery
                        if (selectedItem.type === 'gallery') {
                          await refetchGalleryImages();
                        }
                        // Refresh the main approval queue
                        await refreshQueue();
                        setIsDetailDialogOpen(false);
                        toast({
                          title: 'Rejected',
                          description: `${selectedItem.type} rejected successfully`,
                          variant: 'default'
                        });
                      }
                    }}
                  >
                    <ThumbsDown className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    variant="outline"
                    className="text-accent hover:bg-accent/10"
                    onClick={async () => {
                      const success = await handleFlag(selectedItem);
                      if (success) {
                        // Refresh gallery images if we just flagged a gallery
                        if (selectedItem.type === 'gallery') {
                          await refetchGalleryImages();
                        }
                        // Refresh the main approval queue
                        await refreshQueue();
                        setIsDetailDialogOpen(false);
                        toast({
                          title: 'Flagged',
                          description: `${selectedItem.type} flagged for review`,
                          variant: 'default'
                        });
                      }
                    }}
                  >
                    <Flag className="w-4 h-4 mr-2" />
                    Flag for Review
                  </Button>
                </div>
              </div>

              {/* Approval History */}
              {selectedItem && (
                <ApprovalHistory
                  entityType={selectedItem.type}
                  entityId={selectedItem.id}
                  title="Approval History"
                  limit={10}
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Action Dialog */}
      <ApprovalActionDialog
        isOpen={showActionDialog}
        onClose={handleDialogClose}
        onConfirm={handleActionConfirm}
        entityType={pendingActionItem?.type || 'gallery'}
        entityTitle={pendingActionItem?.title}
        defaultAction={pendingAction || 'approve'}
      />
    </div>
  );
}
